import { useState, useEffect, useCallback } from 'react';
import { authApi } from '@/lib/api';
import i18n from '@/i18n';

export type UserRole = 'super_admin' | 'company_admin' | 'user';

export interface AuthUser {
  id: string;
  email: string;
  role: UserRole;
  company_id: string | null;
  company_name: string | null;
  api_environment?: 'prod' | 'beta';
  must_change_password: boolean;
}

interface SessionInfo {
  ip_address: string;
  last_activity: string;
}

export interface LoginResult {
  success: boolean;
  error?: string;
  sessionInfo?: SessionInfo;
  user?: AuthUser;
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
}

const AUTH_API_URL = authApi();

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
});

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
  });

  // Validate existing session on mount
  useEffect(() => {
    const validateSession = async () => {
      // Try cookie-based validation first (no token needed in body)
      // Also try localStorage for backwards compatibility
      const token = localStorage.getItem('auth_token');

      try {
        const response = await fetch(AUTH_API_URL, {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include', // Send cookies
          body: JSON.stringify({ action: 'validate', token }),
        });

        const data = await response.json();

        if (data.valid && data.user) {
          setState({
            isAuthenticated: true,
            isLoading: false,
            user: data.user,
          });
        } else {
          localStorage.removeItem('auth_token');
          setState({ isAuthenticated: false, isLoading: false, user: null });
        }
      } catch (error) {
        console.error('Session validation error:', error);
        localStorage.removeItem('auth_token');
        setState({ isAuthenticated: false, isLoading: false, user: null });
      }
    };

    validateSession();
  }, []);

  // Listen for auth token cleared events (e.g., from 401 responses)
  useEffect(() => {
    const handleTokenCleared = () => {
      console.log('Auth: Token cleared event received, logging out');
      setState({ isAuthenticated: false, isLoading: false, user: null });
    };

    window.addEventListener('auth-token-cleared', handleTokenCleared);
    return () => window.removeEventListener('auth-token-cleared', handleTokenCleared);
  }, []);

  // Login with email and password
  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    try {
      const response = await fetch(AUTH_API_URL, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include', // Receive cookies
        body: JSON.stringify({ action: 'login', email, password }),
      });

      const data = await response.json();

      if (data.success && data.user) {
        // Store token for backwards compatibility (cookie is primary now)
        if (data.token) {
          localStorage.setItem('auth_token', data.token);
        }
        setState({
          isAuthenticated: true,
          isLoading: false,
          user: data.user,
        });
        return { success: true, user: data.user };
      }

      if (data.error === 'active_session' && data.sessionInfo) {
        return {
          success: false,
          error: 'active_session',
          sessionInfo: data.sessionInfo,
        };
      }

      return { success: false, error: data.error || i18n.t('auth.loginFailed', { ns: 'errors' }) };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: i18n.t('auth.connectionError', { ns: 'errors' }) };
    }
  }, []);

  // Force login (terminate existing session)
  const forceLogin = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    try {
      const response = await fetch(AUTH_API_URL, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ action: 'force_login', email, password }),
      });

      const data = await response.json();

      if (data.success && data.user) {
        if (data.token) {
          localStorage.setItem('auth_token', data.token);
        }
        setState({
          isAuthenticated: true,
          isLoading: false,
          user: data.user,
        });
        return { success: true, user: data.user };
      }

      return { success: false, error: data.error || i18n.t('auth.loginFailed', { ns: 'errors' }) };
    } catch (error) {
      console.error('Force login error:', error);
      return { success: false, error: i18n.t('auth.connectionError', { ns: 'errors' }) };
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    
    try {
      await fetch(AUTH_API_URL, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ action: 'logout', token }),
      });
    } catch (error) {
      console.error('Logout error:', error);
    }

    localStorage.removeItem('auth_token');
    setState({ isAuthenticated: false, isLoading: false, user: null });
  }, []);

  // Change password
  const changePassword = useCallback(async (
    newPassword: string, 
    currentPassword?: string
  ): Promise<{ success: boolean; error?: string }> => {
    const token = localStorage.getItem('auth_token');

    try {
      const response = await fetch(AUTH_API_URL, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ 
          action: 'change_password', 
          token, 
          new_password: newPassword,
          current_password: currentPassword 
        }),
      });

      const data = await response.json();

      if (data.success) {
        setState(prev => ({
          ...prev,
          user: prev.user ? { ...prev.user, must_change_password: false } : null,
        }));
        return { success: true };
      }

      return { success: false, error: data.error || i18n.t('auth.passwordChangeFailed', { ns: 'errors' }) };
    } catch (error) {
      console.error('Change password error:', error);
      return { success: false, error: i18n.t('auth.connectionError', { ns: 'errors' }) };
    }
  }, []);

  // Helper functions for role checking
  const isSuperAdmin = useCallback(() => state.user?.role === 'super_admin', [state.user]);
  const isCompanyAdmin = useCallback(() => state.user?.role === 'company_admin', [state.user]);
  const isUser = useCallback(() => state.user?.role === 'user', [state.user]);

  // Get auth token for API calls
  const getToken = useCallback(() => localStorage.getItem('auth_token'), []);

  return {
    ...state,
    login,
    forceLogin,
    logout,
    changePassword,
    isSuperAdmin,
    isCompanyAdmin,
    isUser,
    getToken,
  };
}
